import { useEffect, useRef, useState } from 'react';
import { formatPercentInput, parsePercentInput } from '@brimo/shared';
import { TextField, Toggle } from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import type { PkvSettings, PkvSettingsPatch } from './api';
import styles from './OrganizationTreePage.module.css';

type NumberKey =
  'pkvCalculationDailyLimit' | 'pkvDefaultMbFactor' | 'pkvDefaultRetentionPct' | 'rvaInsurerDeadlineDays';
type SwitchKey = 'pkvFunnelEnabled' | 'rvaFourEyesAnonymization' | 'rvaFourEyesRelease';

interface NumberSpec {
  key: NumberKey;
  label: string;
  suffix?: string | undefined;
  /** `integer`: whole numbers, required; `decimal`: up to three decimals, empty clears the default. */
  kind: 'integer' | 'decimal';
  hint?: string | undefined;
}

export const PKV_NUMBER_FIELDS: readonly NumberSpec[] = [
  {
    key: 'pkvCalculationDailyLimit',
    label: 'Tageslimit Rechner',
    kind: 'integer',
    hint: 'Berechnungen je Nutzer und Tag',
  },
  { key: 'rvaInsurerDeadlineDays', label: 'Frist Versicherer', suffix: 'Tage', kind: 'integer' },
  { key: 'pkvDefaultMbFactor', label: 'MB-Faktor (Vorgabe)', kind: 'decimal' },
  { key: 'pkvDefaultRetentionPct', label: 'Einbehalt (Vorgabe)', suffix: '%', kind: 'decimal' },
];

export const PKV_SWITCHES: readonly { key: SwitchKey; label: string }[] = [
  { key: 'pkvFunnelEnabled', label: 'Funnel-Link aktiv' },
  { key: 'rvaFourEyesAnonymization', label: 'Vier-Augen-Prüfung der Anonymisierung' },
  { key: 'rvaFourEyesRelease', label: 'Vier-Augen-Prüfung der Freigabe' },
];

const INTEGER_MISSING = 'Bitte tragen Sie eine ganze Zahl ein, zum Beispiel 10.';
const DECIMAL_MISSING = 'Bitte tragen Sie eine Zahl ein, zum Beispiel 1,5.';

type Texts = Record<NumberKey, string>;

function textOf(value: number | null): string {
  return value === null ? '' : formatPercentInput(value);
}

function textsOf(settings: PkvSettings): Texts {
  return {
    pkvCalculationDailyLimit: String(settings.pkvCalculationDailyLimit),
    rvaInsurerDeadlineDays: String(settings.rvaInsurerDeadlineDays),
    pkvDefaultMbFactor: textOf(settings.pkvDefaultMbFactor),
    pkvDefaultRetentionPct: textOf(settings.pkvDefaultRetentionPct),
  };
}

/** What the owner typed → the value to send; `undefined` means unreadable. */
export function parsePkvField(spec: NumberSpec, text: string): number | null | undefined {
  const trimmed = text.trim();
  if (spec.kind === 'integer') return /^\d+$/.test(trimmed) ? Number(trimmed) : undefined;
  if (trimmed === '') return null;
  return parsePercentInput(trimmed) ?? undefined;
}

export interface PkvDefaultsFormProps {
  settings: PkvSettings;
  /** `PATCH /settings/organization/pkv` with one field; resolves with the stored settings. */
  save: (patch: PkvSettingsPatch) => Promise<PkvSettings>;
  /** „Gespeichert · HH:MM" of the settings frame. */
  onSaved: () => void;
}

/**
 * „PKV-Vorgaben" (PKV M0, D-288): auto-save per field like „Absender und Anschrift" (Einstellungen 20). A number
 * field saves on blur when it changed, a switch on change; the server's field message stays under its field.
 */
export function PkvDefaultsForm({ settings, save, onSaved }: PkvDefaultsFormProps) {
  const [texts, setTexts] = useState<Texts>(() => textsOf(settings));
  const [errors, setErrors] = useState<Partial<Record<NumberKey | SwitchKey, string>>>({});
  const [busy, setBusy] = useState<SwitchKey | null>(null);
  const saved = useRef<PkvSettings>(settings);

  // A realtime refresh (another admin) replaces the fields that are not being edited.
  useEffect(() => {
    const previous = textsOf(saved.current);
    const next = textsOf(settings);
    saved.current = settings;
    setTexts((current) => {
      const merged = { ...current };
      for (const key of Object.keys(next) as NumberKey[]) {
        if (current[key] === previous[key]) merged[key] = next[key];
      }
      return merged;
    });
  }, [settings]);

  function setError(key: NumberKey | SwitchKey, message: string | null) {
    setErrors((current) => {
      const copy = { ...current };
      if (message === null) delete copy[key];
      else copy[key] = message;
      return copy;
    });
  }

  async function send(key: NumberKey | SwitchKey, patch: PkvSettingsPatch): Promise<PkvSettings | null> {
    try {
      const next = await save(patch);
      setError(key, null);
      onSaved();
      return next;
    } catch (error) {
      const message = error instanceof ApiError ? (error.fields[key] ?? error.message) : errorMessage(error);
      setError(key, message);
      return null;
    }
  }

  async function commit(spec: NumberSpec) {
    const text = texts[spec.key];
    if (text.trim() === textsOf(saved.current)[spec.key].trim()) {
      setError(spec.key, null);
      return;
    }
    const value = parsePkvField(spec, text);
    if (value === undefined) {
      setError(spec.key, spec.kind === 'integer' ? INTEGER_MISSING : DECIMAL_MISSING);
      return;
    }
    const next = await send(spec.key, { [spec.key]: value });
    if (next) {
      saved.current = next;
      setTexts((current) => ({ ...current, [spec.key]: textsOf(next)[spec.key] }));
    }
  }

  async function flip(key: SwitchKey, value: boolean) {
    setBusy(key);
    const next = await send(key, { [key]: value });
    if (next) saved.current = next;
    setBusy(null);
  }

  return (
    <div className={styles.pkvForm}>
      <div className={styles.pkvFields}>
        {PKV_NUMBER_FIELDS.map((spec) => (
          <TextField
            key={spec.key}
            label={spec.label}
            inputMode={spec.kind === 'integer' ? 'numeric' : 'decimal'}
            value={texts[spec.key]}
            optional={spec.kind === 'decimal'}
            hint={spec.hint}
            error={errors[spec.key]}
            trailing={spec.suffix ? <span className={styles.suffix}>{spec.suffix}</span> : undefined}
            onChange={(event) => {
              const value = event.target.value;
              setTexts((current) => ({ ...current, [spec.key]: value }));
            }}
            onBlur={() => void commit(spec)}
          />
        ))}
      </div>
      <div className={styles.pkvSwitches}>
        {PKV_SWITCHES.map((item) => (
          <div key={item.key} className={styles.switchRow}>
            <Toggle
              size="large"
              label={item.label}
              checked={settings[item.key]}
              disabled={busy === item.key}
              onChange={(value) => void flip(item.key, value)}
            />
            {errors[item.key] ? (
              <span className={styles.fieldError} role="alert">
                {errors[item.key]}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
